/**
 * localStorage ベースのデータ永続化ユーティリティ
 *
 * 実績・統計データを localStorage に保存する。
 * 既存の Cookie データがあれば自動マイグレーションを行う。
 */

// ============================================================================
// 型定義
// ============================================================================

export interface AchievementStatus {
  unlocked: boolean;
  unlockedAt: string;
}

/** mapType → achievementId → status */
export type AchievementData = Record<string, Record<string, AchievementStatus>>;

export interface MapStatistics {
  playCount: number;
  clearCount: number;
  noUndoClearCount: number;
  bestScore: number;
  sumCash: number;
  sumShares: number;
  sumLoco: number;
  sumLinks: number;
  // Seattle 追加
  sumCargoLv?: number;
  sumDelivered?: number;
  sumRemaining?: number;
  sumUnconnected?: number;
}

/** mapType → statistics */
export type StatisticsData = Record<string, MapStatistics>;

export interface AosLocalData {
  achievements: AchievementData;
  statistics: StatisticsData;
}

// ============================================================================
// 定数
// ============================================================================

const STORAGE_KEY = 'aos_data';
const COOKIE_NAME = 'aos_achievements';

// ============================================================================
// コア関数
// ============================================================================

export function loadData(): AosLocalData {
  // 初回アクセス時に Cookie マイグレーションを試行
  migrateCookieIfNeeded();

  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return { achievements: {}, statistics: {} };
  try {
    const parsed = JSON.parse(raw);
    return {
      achievements: parsed.achievements || {},
      statistics: parsed.statistics || {},
    };
  } catch {
    return { achievements: {}, statistics: {} };
  }
}

export function saveData(data: AosLocalData): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

// ============================================================================
// 実績ヘルパー
// ============================================================================

export function loadAchievements(): AchievementData {
  return loadData().achievements;
}

export function saveAchievements(achievements: AchievementData): void {
  const data = loadData();
  data.achievements = achievements;
  saveData(data);
}

// ============================================================================
// 統計ヘルパー
// ============================================================================

export function loadStatistics(): StatisticsData {
  return loadData().statistics;
}

export function saveStatistics(statistics: StatisticsData): void {
  const data = loadData();
  data.statistics = statistics;
  saveData(data);
}

// ============================================================================
// Cookie → localStorage マイグレーション
// ============================================================================

let migrated = false;

function migrateCookieIfNeeded(): void {
  if (migrated) return;
  migrated = true;

  // localStorage に既にデータがあればスキップ
  if (localStorage.getItem(STORAGE_KEY)) return;

  // Cookie から読み込み
  const cookieValue = getCookieValue(COOKIE_NAME);
  if (!cookieValue) return;

  try {
    const achievements: AchievementData = JSON.parse(cookieValue);
    const data: AosLocalData = { achievements, statistics: {} };
    saveData(data);
    // Cookie を削除
    deleteCookie(COOKIE_NAME);
  } catch {
    // パース失敗時は無視
  }
}

function getCookieValue(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

function deleteCookie(name: string): void {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
}

// ============================================================================
// ペンディングスコア（送信失敗時の一時保存）
// ============================================================================

const PENDING_SCORE_KEY = 'aos_pending_score';

export interface PendingScore {
  playerName: string;
  mapType: string;
  finalScore: number;
  turnCount: number;
  metadata: Record<string, unknown>;
  replayData: { initialState: unknown; moves: unknown[] };
  savedAt: string;
}

export function savePendingScore(data: PendingScore): void {
  localStorage.setItem(PENDING_SCORE_KEY, JSON.stringify(data));
}

export function loadPendingScore(): PendingScore | null {
  const raw = localStorage.getItem(PENDING_SCORE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearPendingScore(): void {
  localStorage.removeItem(PENDING_SCORE_KEY);
}
